import React, { createContext, useContext, useCallback } from 'react';

import * as campers from '@gleamdesign-okinawa/camper-interface-public/api';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { AxiosRequestConfig } from 'axios';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

import toast from 'utils/toast';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
  queryCache: new QueryCache({
    onError: (_, query) => {
      if (typeof query.meta?.errorMessage === 'string') {
        toast.error(query.meta.errorMessage);
      }
    },
  }),
  mutationCache: new MutationCache(),
});

const campersApiClient = {
  ...campers.CarUserServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.InquiryUserServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.ReserveUserServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.SpotUserServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.CarMasterUserServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.UserServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.SpotMasterUserServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.ReviewUserServiceApiFactory(undefined, __BACKEND_BASE_URL__),
};

type ApiClient = typeof campersApiClient;

export const ApiClientContext = createContext<ApiClient | undefined>(undefined);

export const ApiClientProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  return (
    <ApiClientContext.Provider value={campersApiClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ApiClientContext.Provider>
  );
};

export const useApiClient = <TPayload = any, TResult = any>(
  key: string,
  config?: AxiosRequestConfig
) => {
  const apiClient = useContext<ApiClient | undefined>(ApiClientContext);
  if (!apiClient) {
    throw new Error('API client is not set');
  }

  const api = (
    apiClient as unknown as Record<
      string,
      (payload: TPayload, config?: unknown) => Promise<{ data: TResult }>
    >
  )[key];
  if (!api) {
    throw new Error(`API method is not available: ${key}`);
  }
  const call = useCallback(
    async (payload: TPayload): Promise<TResult> => {
      const auth = getAuth();
      await new Promise((resolve, reject) =>
        onAuthStateChanged(auth, resolve, reject)
      );
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) {
        throw new Error('No token provided');
      }

      return await api(payload, {
        ...config,
        headers: {
          ...config?.headers,
          Authorization: `Bearer ${token}`,
        },
      }).then((res) => res.data as TResult);
    },
    [api, config]
  );

  return call;
};

const camperPublicApiClient = {
  ...campers.ArticlePublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.CarPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.ContactPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.FaqPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.NewsPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.OwnerInterviewPublicServiceApiFactory(
    undefined,
    __BACKEND_BASE_URL__
  ),
  ...campers.CarMasterPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.SpotMasterPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.ReviewPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.SpotPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
  ...campers.TermsPublicServiceApiFactory(undefined, __BACKEND_BASE_URL__),
};

type PublicApiClient = typeof camperPublicApiClient;

export const PublicApiClientContext = createContext<
  PublicApiClient | undefined
>(undefined);

export const PublicApiClientProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  return (
    <PublicApiClientContext.Provider value={camperPublicApiClient}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </PublicApiClientContext.Provider>
  );
};

export const usePublicApiClient = <TPayload = any, TResult = any>(
  key: string,
  config?: AxiosRequestConfig
) => {
  const publicApiClient = useContext<PublicApiClient | undefined>(
    PublicApiClientContext
  );
  if (!publicApiClient) {
    throw new Error('API client is not set');
  }

  const api = (
    publicApiClient as unknown as Record<
      string,
      (payload: TPayload, config?: unknown) => Promise<{ data: TResult }>
    >
  )[key];
  if (!api) {
    throw new Error(`Public API method is not available: ${key}`);
  }
  const call = useCallback(
    async (payload: TPayload): Promise<TResult> => {
      return await api(payload as never, {
        config,
      }).then((res) => res.data as TResult);
    },
    [api, config]
  );

  return call;
};
